<script setup lang="ts">
import { ref, computed, type Ref, onMounted } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import { useFormsStore } from '@/domains/home/services/nir/configurations/stores/forms.ts'
import { dateTimeFormat } from '@/commons/composables/transformations.ts'
import type { Field } from '@/domains/home/services/nir/configurations/types/forms.ts'

type FormField = {
  fieldName: string
  label: string
  type: string
  cardinality: string
  class: string
  isMandatory: boolean
  isActive: boolean
  min:number
  max:number
  regex: string
  options: FormFieldOption[]
}

type FormFieldOption = {
  label: string
  value: string
  position: number
  isActive: boolean
}

const createFieldModalOpen = ref(false)
const validationTypeModalOpen = ref(false)
const deleteConfirmModalOpen = ref(false)
const selectedEditId = ref('')
const loading = ref(false)

const form = ref<FormField>({
  fieldName: '',
  label: '',
  type: 'text',
  cardinality: 'SINGLE',
  class: 'BASE',
  isMandatory: false,
  isActive: true,
  min: 1,
  max: 10,
  regex:"",
  options: [],
})

const validationTypeForm = ref({
  id: '',
  type: 'NONE'
})

const fieldTypes = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Button' },
  { value: 'country', label: 'Country' },
  { value: 'contact', label: 'Contact' },
  { value: 'postal-address', label: 'Postal Address' },
  { value: 'isic', label: 'ISIC' },
  { value: 'admin-units', label: 'Admin Units' },
  { value: 'individuals', label: 'Individuals' },
  { value: 'corporates', label: 'Corporates' },
  { value: 'beneficial-owners', label: 'Beneficial Owners' },
]

const cardinalities = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MULTIPLE', label: 'Multiple' },
]

const validationTypes = [
  { value: 'none', label: 'None' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'nin', label: 'National ID Number' },
  { value: 'admin-units', label: 'Administrative Units' },
  { value: 'isic', label: 'Nature of Business' },
  { value: 'individual', label: 'Individual' },
  { value: 'contact', label: 'Contact' },
]

const classes = [
  { value: 'BASE', label: 'Base' },
  { value: 'COMPOSITE', label: 'Composite' },
]

const showOptionsField = computed(() => {
  return (
    form.value.type === 'select' || form.value.type === 'radio' || form.value.type === 'checkbox'
  )
})

function createFormField() {
  loading.value = true

  if (selectedEditId.value) {
    let data = form.value
    let payload = {
      id: selectedEditId.value,
      ...data,
    }
    store.updateField(payload).then(() => {
      loading.value = false
      createFieldModalOpen.value = false
      resetFieldForm()
      fetch()
    })
  } else {
    store.createField(form.value).then(() => {
      loading.value = false
      createFieldModalOpen.value = false
      resetFieldForm()
      fetch()
    })
  }
}

function removeField() {
  loading.value = true
  store.removeField({ id: selectedEditId.value }).then(() => {
    loading.value = false
    deleteConfirmModalOpen.value = false
    fetch()
  })
}

function updateValidationType() {
  loading.value = true
  store.updateValidationType(validationTypeForm.value)
      .then(() => {
        loading.value = false
        validationTypeModalOpen.value = false
        fetch()
      })
}

function resetFieldForm() {
  form.value.fieldName = ''
  form.value.label = ''
  form.value.type = 'text'
  form.value.isMandatory = false
  form.value.isActive = false
  form.value.options = []
}

function editField(field: Field) {
  selectedEditId.value = field.id
  form.value.label = field.label
  form.value.fieldName = field.name
  form.value.type = field.type || 'text'
  form.value.isMandatory = field.isMandatory
  form.value.isActive = field.isActive
  form.value.min = field.min
  form.value.max = field.max
  form.value.regex = field.regex

  createFieldModalOpen.value = true
}

function editValidation(field: Field) {
  validationTypeForm.value.id = field.id
  validationTypeForm.value.type = field.validationType || 'NONE'
  validationTypeModalOpen.value = true
}

function deleteField(field: Field) {
  selectedEditId.value = field.id
  deleteConfirmModalOpen.value = true
}

function getStatusColor(status: boolean) {
  return status ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-50'
}

const store = useFormsStore()
onMounted(() => {
  fetch()
})

function fetch() {
  loading.value = true
  store.fetchFields(1, 1000).then(() => {
    loading.value = false
  })
}

function add() {
  selectedEditId.value = ''
  createFieldModalOpen.value = true
}

function cancel() {
  createFieldModalOpen.value = false
  resetFieldForm()
}
</script>

<template>
  <div class="flex-1 flex flex-col">
    <!-- Action Bar -->
    <div class="flex items-center justify-end mb-4">
      <button
        @click="add"
        class="btn-primary bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded text-sm font-medium transition flex items-center space-x-2"
      >
        <i class="fa-solid fa-plus"></i>
        <span>Add Form Field</span>
      </button>
    </div>

    <!-- Form Fields Table -->
    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                #
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Label
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Field Name
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Type
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Configurations
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Validation
              </th>
              <th
                class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"
              ></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(field, idx) in store.fields"
              :key="field.id"
              class="hover:bg-gray-50 transition"
              :class="field.deletedAt ? 'bg-red-100' : ''">
              <td class="py-3 px-4 border-b border-gray-200 font-semibold">
                <span class="text-gray-900">{{ idx + 1 }}</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 font-semibold">
                <span class="text-gray-900">{{ field.label }}</span>
                <span
                  v-if="field.deletedAt"
                  class="text-white bg-red-600 px-2 py-0.5 text-[9px] mx-1 rounded-lg"
                  >deleted</span>

                <span v-if="field.isMandatory" class="text-red-600">
                  <i class="fa-solid fa-asterisk text-xs"></i>
                </span>
                <span v-else class="text-gray-400">
                  <i class="fa-solid fa-minus text-xs"></i>
                </span>

                <span v-if="field.isActive" class="text-success-600">
                  <i class="fa-solid fa-check text-xs"></i>
                </span>
                <span v-else class="text-gray-400">
                  <i class="fa-solid fa-ban text-xs"></i>
                </span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="font-mono text-gray-900 font-medium bg-gray-50 p-1 rounded">{{ field.name }}</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="px-2 py-1 bg-primary-50 text-blue-700 rounded text-xs font-medium">
                  {{ field.type }}
                </span>
              </td>
              <td class="py-3 px-4 text-left border-b border-gray-200">
                <span class="px-2 py-1 rounded text-xs font-medium">
                  <i :class="field.class === 'BASE' ? 'fas fa-cube' : 'fas fa-cubes'" class="mr-1"></i>
                  {{ field.class }}
                </span>
                <label>
                  <span v-if="field.cardinality" :class="[
                  'px-2 py-1 rounded text-xs font-medium',
                  field.cardinality === 'SINGLE' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'
                ]">
                  <i :class="field.cardinality === 'SINGLE' ? 'fas fa-diamond' : 'fas fa-layer-group'" class="mr-1"></i>
                  {{ field.cardinality }}
                </span>
                  <span v-else class="text-gray-400">--</span>
                </label>
              </td>
              <td class="py-3 px-4 text-left border-b border-gray-200">
                <div class="flex gap-2">
                  <span class="px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1 bg-gray-50 text-gray-600">
                  {{ field.validationType }}
                  <i @click="editValidation(field)" class="fas fa-pen text-[9px] ml-1 cursor-pointer hover:text-primary"></i>
                </span>
                  <label class="flex gap-2 ">
                    <span class="bg-warning-50 border border-warning-400 text-warning-700 p-1 rounded-lg">Max: {{ field.min }} - Min: {{ field.max }}</span>
                    <i class="bg-danger-50 border border-danger-400 text-danger-700 p-1 rounded-lg" v-if="field.regex">{{ field.regex }}</i>
                  </label>
                </div>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center justify-center space-x-2" v-if="!field.deletedAt">
                  <button
                    @click="editField(field)"
                    class="btn-sm-primary"
                    title="Edit"
                  >
                    <i class="fa-solid fa-cog"></i>
                  </button>
                  <button
                    @click="deleteField(field)"
                    class="btn-sm-danger"
                    title="Edit">
                    <i class="fa-solid fa-times"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create Form Field Modal -->
    <AppModal v-model="createFieldModalOpen" xl2>
      <div class="p-2">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Add New Form Field</h2>

        <form @submit.prevent="createFormField" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <!-- Field Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-code text-gray-400 mr-1"></i>
                Field Name (ID) *
              </label>
              <input
                v-model="form.fieldName"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., company_name"
              />
              <p class="text-xs text-gray-500 mt-1">Use lowercase with underscores</p>
            </div>

            <!-- Field Label -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
                Field Label *
              </label>
              <input
                v-model="form.label"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., Company Name"
              />
            </div>

            <!-- Field Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-list text-gray-400 mr-1"></i>
                Field Type *
              </label>
              <select
                v-model="form.type"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Type</option>
                <option v-for="type in fieldTypes" :key="type.value" :value="type.value">
                  {{ type.label }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-asterisk text-gray-400 mr-1"></i>
                Cardinality *
              </label>
              <select
                v-model="form.cardinality"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Cardinality</option>
                <option v-for="item in cardinalities" :key="item.value" :value="item.value">
                  {{ item.label }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-asterisk text-gray-400 mr-1"></i>
                Class *
              </label>
              <select
                v-model="form.class"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Class</option>
                <option v-for="item in classes" :key="item.value" :value="item.value">
                  {{ item.label }}
                </option>
              </select>
            </div>

            <!-- Options (for select, radio, checkbox) -->
            <div
              v-if="showOptionsField && selectedEditId == ''"
              class="col-span-2 border border-gray-200 rounded p-2"
            >
              <div class="flex justify-between">
                <label class="block text-sm font-medium text-gray-700 mb-1.5 my-auto">
                  <i class="fa-solid fa-list-ul text-gray-400 mr-1"></i>
                  Options *
                </label>
                <button
                  type="button"
                  @click="
                    form.options.push({
                      label: '',
                      value: '',
                      position: form.options.length,
                      isActive: true,
                    })
                  "
                  class="btn-primary px-4 py-1 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 transition"
                >
                  <i class="fa-solid fa-plus"></i> Option
                </button>
              </div>

              <div
                class="grid grid-cols-2 gap-2 py-2"
                v-for="(option, idx) in form.options"
                :key="idx"
              >
                <input
                  v-model="option.label"
                  type="text"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Label"
                />
                <input
                  v-model="option.value"
                  type="text"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Value"
                />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2">
              <!-- Mandatory Checkbox -->
              <div class="flex items-center space-x-2">
                <input
                  v-model="form.isMandatory"
                  type="checkbox"
                  id="mandatory"
                  class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label for="mandatory" class="text-sm font-medium text-gray-700">
                  <i class="fa-solid fa-asterisk text-red-600 text-xs mr-1"></i>
                  Mandatory
                </label>
              </div>

              <!-- Active Checkbox -->
              <div class="flex items-center space-x-2">
                <input
                  v-model="form.isActive"
                  type="checkbox"
                  id="active"
                  class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label for="active" class="text-sm font-medium text-gray-700">
                  <i class="fa-solid fa-check-circle text-green-600 text-xs mr-1"></i>
                  Active
                </label>
              </div>
            </div>


          </div>

          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-text-width text-gray-400 mr-1"></i>
                Minimum Length *
              </label>
              <input
                  v-model.number="form.min"
                  type="number"
                  min="0"
                  class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., 3"
              />
              <p class="text-xs text-gray-500 mt-1">Minimum characters required</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-text-width text-gray-400 mr-1"></i>
                Maximum Length *
              </label>
              <input
                  v-model.number="form.max"
                  type="number"
                  min="0"
                  class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., 100"
              />
              <p class="text-xs text-gray-500 mt-1">Maximum characters allowed</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-code text-gray-400 mr-1"></i>
                Regex Pattern
              </label>
              <input
                  v-model="form.regex"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  placeholder="e.g., ^[A-Za-z]+$"
              />
              <p class="text-xs text-gray-500 mt-1">Pattern for validation</p>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="cancel"
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              v-if="selectedEditId"
              type="submit"
              :disabled="loading"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!loading">Update Field</span>
              <span v-else>Updating...</span>
            </button>
            <button
              v-else
              type="submit"
              :disabled="loading"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!loading">Create Field</span>
              <span v-else>Creating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <AppModal v-model="validationTypeModalOpen" sm>
      <div class="p-2">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Update Validation Type</h2>

        <form @submit.prevent="updateValidationType" class="space-y-4">
          <div class="grid grid-cols-1 gap-4">
            <!-- Field Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-list text-gray-400 mr-1"></i>
                Validation Type *
              </label>
              <select
                  v-model="validationTypeForm.type"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
                <option value="">Select Type</option>
                <option v-for="type in validationTypes" :key="type.value" :value="type.value">
                  {{ type.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
                type="button"
                @click="validationTypeModalOpen = false"
                class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
                type="submit"
                :disabled="loading"
                class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!loading">Update Validation</span>
              <span v-else>Updating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <AppModal v-model="deleteConfirmModalOpen" xl2>
      <div class="p-2" id="delete-confirm">
        <div class="text-center py-4">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
          >
            <i class="fa-solid fa-exclamation-triangle text-red-600 text-xl"></i>
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-2">Delete Master Data Field</h3>
          <p class="text-sm text-gray-600 mb-6">
            Are you sure you want to delete this master data field? This action cannot be undone.
          </p>

          <div class="flex items-center justify-center space-x-3">
            <button
              type="button"
              @click="deleteConfirmModalOpen = false"
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="button"
              @click="removeField"
              class="btn-primary px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition"
            >
              <i class="fa-solid fa-trash mr-1"></i>
              <span v-if="!loading">Confirm Delete</span>
              <span v-else>Deleting...</span>
            </button>
          </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
